import { datadogRum } from '@datadog/browser-rum';
import { useSetAtom } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { invalidateCertificateQueries } from '@/utils/queryInvalidation';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { queryClient } from '@/services';

import { assignUsersToChecklist2 } from '@/_generated/api-clients/content';
import {
  getPlanAndAssignmentsQueryKey,
  listEnrollmentsByCourseQueryKey,
  unassignUsers,
} from '@/_generated/api-clients/xp';
import { toggleRefetchDispatchAtom } from '@/routes/TrainingPlans/ManagePlans/state';

interface AssignPayload {
  userIds: string[];
  assignCourse: boolean;
}

interface UnassignTeamMemberProps {
  activeTeamMember: { id: string; name: string };
  isCompliance: boolean;
  isOpen: boolean;
  onClose: () => void;
  planId: string;
}

const UnassignTeamMember: React.FC<UnassignTeamMemberProps> = ({
  activeTeamMember,
  isCompliance,
  isOpen,
  onClose,
  planId,
}) => {
  const { t } = useTranslation();
  const toggle = useSetAtom(toggleRefetchDispatchAtom);

  const onAssignTeamMembersCompliance = (
    members: string | string[],
    unassign?: boolean,
  ) => {
    const userIds = Array.isArray(members)
      ? Array.from(new Set(members))
      : [members];
    const payload: AssignPayload = {
      userIds,
      assignCourse: unassign ? false : true,
    };

    const numberOfTeamMembersToAssign = payload.userIds.length;
    assignUsersToChecklist2({ body: payload, path: { courseId: planId } })
      .then(() => {
        toggle('refetchCompliance');
        toggle('refetchCourseReport');
        queryClient.invalidateQueries({
          queryKey: listEnrollmentsByCourseQueryKey({
            path: { courseId: planId },
          }),
        });
        queryClient.invalidateQueries({
          queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }),
        });

        const toastMessage = unassign
          ? t('TrainingPlans.toastMessage.unassigned', {
              teamMember: activeTeamMember.name,
            })
          : numberOfTeamMembersToAssign === 1
            ? t('TrainingPlans.toastMessage.assignedTeamMember')
            : t('TrainingPlans.toastMessage.assignedTeamMembers', {
                count: numberOfTeamMembersToAssign,
              });

        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>{toastMessage}</ToastMessageBlock>
        ));
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
    onClose();
  };

  const onUnassignTeamMember = () => {
    const userIds = [activeTeamMember.id];

    if (isCompliance) {
      onAssignTeamMembersCompliance(activeTeamMember.id, true);
    } else {
      unassignUsers({ path: { planId }, body: { userIds } })
        .then(() => {
          toggle('refetchAssignedTeamMembers');
          toggle('refetchAssignedChecklist');
          queryClient.invalidateQueries({
            queryKey: getPlanAndAssignmentsQueryKey({
              path: { planId },
            }),
          });
          invalidateCertificateQueries(planId, userIds);
          toast.custom(toastObj => (
            <ToastMessageBlock id={toastObj.id}>
              {t('TrainingPlans.toastMessage.unassigned', {
                teamMember: activeTeamMember.name,
              })}
            </ToastMessageBlock>
          ));
        })
        .catch((err: unknown) => {
          datadogRum.addError(
            err instanceof Error ? err : new Error(String(err)),
          );
        });
      onClose();
    }
  };

  return (
    <ConfirmationModal
      bodyText={t('TrainingPlans.teamMembers.unassignConfirmation', {
        name: activeTeamMember.name,
      })}
      headerText={t('Generic.unassign')}
      isOpen={isOpen}
      onClose={onClose}
      primaryButtonColor="primary"
      primaryButtonHandler={onUnassignTeamMember}
      primaryButtonText={t('Button.unassign')}
      secondaryButtonHandler={onClose}
      secondaryButtonText={t('Button.cancel')}
    />
  );
};

export default UnassignTeamMember;
